import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtFCFA } from "@/lib/api";
import { DashboardShell, StatusBadge } from "@/components/DashboardShell";
import { PropertyCard } from "@/components/PropertyCard";
import { FileText, Calendar, Heart, MessageSquare, LifeBuoy, Plus, Paperclip, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

export default function ClientDashboard() {
  const [tab, setTab] = useState("demandes");
  const [requests, setRequests] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [tickets, setTickets] = useState([]);

  const loadFavs = async () => { const { data } = await api.get("/favorites"); setFavorites(data); };
  const loadTickets = async () => { try { const { data } = await api.get("/support/tickets/mine"); setTickets(data); } catch {} };
  useEffect(() => {
    (async () => {
      try {
        const [r, res] = await Promise.all([api.get("/requests/mine"), api.get("/reservations/mine")]);
        setRequests(r.data); setReservations(res.data);
      } catch {}
      loadFavs(); loadTickets();
    })();
  }, []);

  const tabs = [
    { value: "demandes", label: `Mes demandes (${requests.length})` },
    { value: "reservations", label: `Mes réservations (${reservations.length})` },
    { value: "favoris", label: `Mes favoris (${favorites.length})` },
    { value: "comparer", label: "Comparateur" },
    { value: "support", label: `Support (${tickets.length})` },
  ];

  return (
    <DashboardShell title="Espace Client" subtitle="Suivez vos demandes, réservations et biens favoris." tabs={tabs} active={tab} onTab={setTab}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Stat icon={FileText} label="Demandes" value={requests.length} />
        <Stat icon={Calendar} label="Réservations" value={reservations.length} />
        <Stat icon={Heart} label="Favoris" value={favorites.length} />
        <Stat icon={MessageSquare} label="En traitement" value={requests.filter((r) => r.status === "En traitement").length} />
      </div>

      {tab === "demandes" && (
        <div className="rounded-xl bg-white border border-black/5 overflow-hidden">
          {requests.length === 0 ? <Empty text="Aucune demande pour le moment." cta /> : (
            <table className="w-full text-sm">
              <thead className="bg-ivory text-left text-slate-500"><tr>
                <th className="px-5 py-3 font-medium">Référence</th><th className="px-5 py-3 font-medium">Type</th>
                <th className="px-5 py-3 font-medium">Date</th><th className="px-5 py-3 font-medium">Conseiller</th><th className="px-5 py-3 font-medium">Statut</th>
              </tr></thead>
              <tbody>
                {requests.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`client-req-${r.reference}`}>
                    <td className="px-5 py-3 font-mono text-xs text-gold">{r.reference}</td>
                    <td className="px-5 py-3 capitalize">{r.type}</td>
                    <td className="px-5 py-3 text-slate-500">{new Date(r.created_at).toLocaleDateString("fr-FR")}</td>
                    <td className="px-5 py-3 text-slate-500">{r.advisor || "—"}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "reservations" && (
        <div className="rounded-xl bg-white border border-black/5 overflow-hidden">
          {reservations.length === 0 ? <Empty text="Aucune réservation." /> : (
            <table className="w-full text-sm">
              <thead className="bg-ivory text-left text-slate-500"><tr>
                <th className="px-5 py-3 font-medium">Référence</th><th className="px-5 py-3 font-medium">Logement</th>
                <th className="px-5 py-3 font-medium">Arrivée</th><th className="px-5 py-3 font-medium">Départ</th><th className="px-5 py-3 font-medium">Statut</th>
              </tr></thead>
              <tbody>
                {reservations.map((r) => (
                  <tr key={r.id} className="border-t" data-testid={`client-res-${r.reference}`}>
                    <td className="px-5 py-3 font-mono text-xs text-gold">{r.reference}</td>
                    <td className="px-5 py-3">{r.property_title}</td>
                    <td className="px-5 py-3 text-slate-500">{r.check_in}</td>
                    <td className="px-5 py-3 text-slate-500">{r.check_out}</td>
                    <td className="px-5 py-3"><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === "favoris" && (
        favorites.length === 0 ? <div className="rounded-xl bg-white border border-black/5"><Empty text="Aucun bien favori." cta /></div> : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {favorites.map((p) => <PropertyCard key={p.id} property={p} favored onFavChange={loadFavs} />)}
          </div>
        )
      )}
      {tab === "comparer" && <ComparePanel favorites={favorites} />}
      {tab === "support" && (
        <>
          <div className="flex justify-end mb-4"><NewTicketDialog onSaved={loadTickets} /></div>
          {tickets.length === 0 ? <div className="rounded-xl bg-white border border-black/5"><Empty text="Aucun ticket de support." /></div> : (
            <div className="space-y-4">
              {tickets.map((t) => (
                <div key={t.id} className="rounded-xl bg-white border border-black/5 p-5" data-testid={`my-ticket-${t.reference}`}>
                  <div className="flex items-center justify-between">
                    <div><span className="font-mono text-xs text-gold">{t.reference}</span><h4 className="font-semibold text-navy">{t.subject}</h4></div>
                    <StatusBadge status={t.status} />
                  </div>
                  <p className="mt-2 text-sm text-slate-600">{t.message}</p>
                  <TicketAttachments files={t.attachments} />
                  {(t.replies || []).length > 0 && <div className="mt-3 space-y-2 border-t pt-3">{t.replies.map((r, i) => (<div key={i} className={`rounded-lg p-2.5 text-sm ${r.role === "client" ? "bg-ivory" : "bg-navy/5"}`}><span className="text-xs text-slate-400">{r.author}: </span>{r.message}<TicketAttachments files={r.attachments} /></div>))}</div>}
                </div>))}
            </div>
          )}
        </>
      )}
    </DashboardShell>
  );
}

const TicketAttachments = ({ files }) => !files || files.length === 0 ? null : (
  <div className="mt-2 flex flex-wrap gap-2">
    {files.map((u, i) => /\.(png|jpe?g|webp|gif)(\?|$)/i.test(u)
      ? <a key={i} href={u} target="_blank" rel="noreferrer"><img src={u} alt="pièce jointe" className="h-16 w-16 rounded-lg object-cover border" /></a>
      : <a key={i} href={u} target="_blank" rel="noreferrer" className="text-xs text-gold underline">Document {i + 1}</a>)}
  </div>
);

function NewTicketDialog({ onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ subject: "", category: "Général", priority: "Normale", message: "" });
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const upload = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const file of Array.from(files)) {
        const fd = new FormData(); fd.append("file", file);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
      }
      setAttachments((a) => [...a, ...urls]);
      toast.success("Pièce(s) jointe(s) ajoutée(s)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const removeAt = (i) => setAttachments((a) => a.filter((_, idx) => idx !== i));
  const save = async (e) => { e.preventDefault(); try { await api.post("/support/tickets", { ...f, attachments }); toast.success("Ticket créé"); setOpen(false); setF({ subject: "", category: "Général", priority: "Normale", message: "" }); setAttachments([]); onSaved(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="bg-navy text-white hover:bg-navy-600 rounded-lg" data-testid="new-ticket-btn"><Plus className="h-4 w-4 mr-1.5" /> Nouveau ticket</Button></DialogTrigger>
      <DialogContent><DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Nouveau ticket de support</DialogTitle><DialogDescription>Décrivez votre demande, notre équipe vous répond rapidement.</DialogDescription></DialogHeader>
        <form onSubmit={save} className="space-y-3" data-testid="client-ticket-form">
          <div><Label>Sujet *</Label><Input required value={f.subject} onChange={(e) => set("subject", e.target.value)} className="mt-1.5" data-testid="tk-subject" /></div>
          <div><Label>Message *</Label><Textarea required rows={4} value={f.message} onChange={(e) => set("message", e.target.value)} className="mt-1.5" data-testid="tk-message" /></div>
          <div>
            <Label className="flex items-center gap-1.5"><Paperclip className="h-4 w-4" /> Pièces jointes (image ou document)</Label>
            <input type="file" accept="image/*,.pdf,.doc,.docx" multiple onChange={(e) => e.target.files.length && upload(e.target.files)} className="mt-1.5 block w-full text-sm text-slate-500 file:mr-3 file:rounded-lg file:border-0 file:bg-navy file:px-4 file:py-2 file:text-white hover:file:bg-navy-600" data-testid="tk-upload" />
            {uploading && <p className="mt-1 text-xs text-gold">Téléversement en cours...</p>}
            {attachments.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-2" data-testid="tk-attachments">
                {attachments.map((u, i) => (
                  <span key={i} className="flex items-center gap-1 rounded-lg bg-ivory px-2 py-1 text-xs text-navy">Fichier {i + 1}<button type="button" onClick={() => removeAt(i)} className="text-red-600" data-testid={`tk-remove-${i}`}><X className="h-3 w-3" /></button></span>
                ))}
              </div>
            )}
          </div>
          <Button type="submit" disabled={uploading} className="w-full bg-navy text-white h-11 rounded-lg" data-testid="tk-submit">Envoyer</Button>
        </form></DialogContent>
    </Dialog>
  );
}

function ComparePanel({ favorites }) {
  const [sel, setSel] = useState([]);
  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : (s.length >= 4 ? s : [...s, id]));
  const chosen = favorites.filter((p) => sel.includes(p.id));
  const rows = [
    ["Prix", (p) => fmtFCFA(p.short_term && p.price_night ? p.price_night : p.price)],
    ["Type", (p) => p.type],
    ["Transaction", (p) => p.transaction],
    ["Localisation", (p) => `${p.district ? p.district + ", " : ""}${p.city}`],
    ["Surface", (p) => (p.surface ? `${p.surface} m²` : "—")],
    ["Chambres", (p) => p.bedrooms || "—"],
    ["Salles de bain", (p) => p.bathrooms || "—"],
    ["Parking", (p) => p.parking || "—"],
    ["Statut", (p) => p.status],
  ];
  if (favorites.length === 0)
    return <div className="rounded-xl bg-white border border-black/5 py-16 text-center text-slate-400" data-testid="compare-empty">Ajoutez des biens à vos favoris pour les comparer.</div>;
  return (
    <div className="space-y-6">
      <div className="rounded-xl bg-white border border-black/5 p-5">
        <div className="text-sm text-slate-500 mb-3">Sélectionnez jusqu'à 4 biens à comparer ({sel.length}/4)</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {favorites.map((p) => (
            <label key={p.id} className={`flex items-center gap-3 rounded-lg border p-3 cursor-pointer ${sel.includes(p.id) ? "border-gold bg-gold/5" : "border-black/10"}`} data-testid={`compare-pick-${p.reference}`}>
              <input type="checkbox" checked={sel.includes(p.id)} onChange={() => toggle(p.id)} className="accent-gold h-4 w-4" />
              <img src={p.images?.[0]} alt="" className="h-10 w-10 rounded object-cover" />
              <div className="min-w-0"><div className="text-sm font-medium text-navy line-clamp-1">{p.title}</div><div className="text-xs text-slate-400">{p.reference}</div></div>
            </label>
          ))}
        </div>
      </div>
      {chosen.length >= 2 ? (
        <div className="rounded-xl bg-white border border-black/5 overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]" data-testid="compare-table">
            <thead><tr className="bg-ivory"><th className="px-4 py-3 text-left font-medium text-slate-500">Critère</th>{chosen.map((p) => (<th key={p.id} className="px-4 py-3 text-left"><div className="font-semibold text-navy line-clamp-1">{p.title}</div><div className="text-xs text-gold font-mono">{p.reference}</div></th>))}</tr></thead>
            <tbody>{rows.map(([label, fn]) => (<tr key={label} className="border-t"><td className="px-4 py-3 font-medium text-slate-500">{label}</td>{chosen.map((p) => (<td key={p.id} className="px-4 py-3 text-navy capitalize">{fn(p)}</td>))}</tr>))}</tbody>
          </table>
        </div>
      ) : (
        <div className="text-center text-slate-400 py-10" data-testid="compare-hint">Sélectionnez au moins 2 biens pour afficher la comparaison.</div>
      )}
    </div>
  );
}

const Stat = ({ icon: Icon, label, value }) => (  <div className="rounded-xl bg-white border border-black/5 p-5 flex items-center gap-4">
    <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-gold/15 text-gold"><Icon className="h-5 w-5" /></span>
    <div><div className="font-mono text-2xl font-bold text-navy">{value}</div><div className="text-xs text-slate-400">{label}</div></div>
  </div>
);
const Empty = ({ text, cta }) => (
  <div className="py-16 text-center text-slate-400">
    <p>{text}</p>
    {cta && <Link to="/acheter" className="mt-3 inline-block text-sm font-medium text-gold hover:underline">Explorer les biens →</Link>}
  </div>
);
